import { SummaryStatsCard } from '@exam-center/components';
import {
  formatExamDate,
  formatRoomData,
  sortRooms,
  useExamEventDetails,
} from '@exam-center/exam-event-management';
import { useGetExamEventResultSummaryQuery } from '@exam-center/graphql';
import { Flex, Grid, Paper, Text } from '@shared/components';
import { useMemo } from 'react';

export default function ExamEventResultSummary() {
  const { examEvent } = useExamEventDetails();
  const { numberOfExaminees, seat, roundNumber, examStartedAt, examEndedAt, examLocation, rooms } =
    examEvent;
  const {
    nameTh: locationName,
    province: { nameTh: provinceName },
  } = examLocation;

  const examRooms = rooms?.map(formatRoomData).sort(sortRooms) ?? [];

  const { data: examEventResultSummaryData, loading: isExamEventResultSummaryLoading } =
    useGetExamEventResultSummaryQuery({
      variables: {
        getExamEventResultSummaryInput: {
          examEventId: examEvent.id,
        },
      },
    });

  const examEventResultSummary = useMemo(() => {
    return (
      examEventResultSummaryData?.ecGetExamEventResultSummary ?? {
        passed: 0,
        failed: 0,
        absent: 0,
        canceled: 0,
      }
    );
  }, [examEventResultSummaryData]);

  return (
    <Paper
      component={Flex}
      sx={(theme) => ({
        flexDirection: 'column',
        p: theme.spacing(6),
        gap: theme.spacing(6),
        borderRadius: theme.shape.rounded(2),
        width: '100%',
      })}
    >
      <Flex
        sx={(theme) => ({
          gap: theme.spacing(4),
        })}
      >
        <SummaryStatsCard
          title='จำนวนผู้สมัคร / ที่นั่งที่เปิดรับ'
          displayValue={`${numberOfExaminees}/${seat} ที่นั่ง`}
          color='warning'
          loading={isExamEventResultSummaryLoading}
          sx={(theme) => ({
            '.MuiTypography-root': {
              color: theme.palette.text.secondary,
            },
            '.MuiTypography-h3': {
              color: theme.palette.text.primary,
            },
          })}
        />
        <SummaryStatsCard
          title='จำนวนผู้ที่สอบผ่าน'
          displayValue={`${examEventResultSummary.passed} คน`}
          color='success'
          loading={isExamEventResultSummaryLoading}
        />
        <SummaryStatsCard
          title='จำนวนผู้ที่สอบไม่ผ่าน'
          displayValue={`${examEventResultSummary.failed} คน`}
          color='error'
          loading={isExamEventResultSummaryLoading}
        />
        <SummaryStatsCard
          title='จำนวนผู้ที่ขาดสอบ'
          displayValue={`${examEventResultSummary.absent} คน`}
          loading={isExamEventResultSummaryLoading}
        />
        <SummaryStatsCard
          title='จำนวนผู้ถูกยกเลิก'
          displayValue={`${examEventResultSummary.canceled} คน`}
          loading={isExamEventResultSummaryLoading}
        />
      </Flex>
      <Grid
        sx={(theme) => ({
          gridTemplateColumns: '120px 1fr',
          rowGap: theme.spacing(2),
          columnGap: theme.spacing(6),
        })}
      >
        <Text>รอบสอบที่</Text>
        <Text variant='body1Semibold'>{roundNumber}</Text>
        <Text>วันที่สอบ:</Text>
        <Text variant='body1Semibold'>{formatExamDate(examStartedAt, examEndedAt)}</Text>
        <Text>สถานที่สอบ:</Text>
        <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(1) })}>
          <Text variant='body1Semibold'>{locationName}</Text>
          {examRooms.map((room) => (
            <Text key={room.roomNumber} variant='body2'>
              อาคาร: {room.building} ชั้น: {room.floor} ห้อง: {room.name} เลขห้อง: {room.roomNumber}
            </Text>
          ))}
        </Flex>
        <Text>จังหวัด:</Text>
        <Text variant='body1Semibold'>{provinceName}</Text>
      </Grid>
    </Paper>
  );
}
